import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { sendQuery } from "../../modules/traceability";
import "./gten-home.css";
import {
  navLinks,
  sectionMap,
  loginLink,
  mapFeed,
  mapOutput,
  lifecycle,
  lenses,
  dashboardTabs,
  insightSteps,
  people,
  fitSteps,
  footerGainssLinks,
  footerExploreLinks,
  aboutText,
} from "./gtenHomeData";

const defaultForm = { name: "", email: "", focus: "" };

const GtenHome = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(dashboardTabs[0].id);
  const [form, setForm] = useState(defaultForm);
  const [status, setStatus] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const tabRefs = useRef({});

  // /approach, /capabilities ... are routes: scroll to the matching section (or top for "/")
  const location = useLocation();
  const firstLoad = useRef(true);
  useEffect(() => {
    const path = location.pathname.slice(1);
    const el = document.getElementById(sectionMap[path] || path);
    // jump instantly on first load (direct URL / refresh), smooth scroll on tab clicks
    const behavior = firstLoad.current ? "auto" : "smooth";
    firstLoad.current = false;
    if (el) el.scrollIntoView({ behavior });
    else window.scrollTo({ top: 0, behavior });
  }, [location]);

  // Google's reCAPTCHA script throws an uncaught "Timeout" when a token expires while the widget sits idle.
  // It is harmless (the widget just needs a re-check), so keep it from surfacing as a runtime error.
  useEffect(() => {
    const isCaptchaTimeout = (msg) => /recaptcha/i.test(String(msg || "")) && /timeout/i.test(String(msg || ""));
    const onError = (e) => {
      if (isCaptchaTimeout(e.message)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    const onRejection = (e) => {
      const msg = e.reason && (e.reason.message || e.reason);
      if (isCaptchaTimeout(msg) || /^timeout$/i.test(String(msg))) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection, true);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection, true);
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const handleTabKeyDown = (e, index) => {
    let next;
    if (e.key === "ArrowRight") next = (index + 1) % dashboardTabs.length;
    if (e.key === "ArrowLeft") next = (index + dashboardTabs.length - 1) % dashboardTabs.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = dashboardTabs.length - 1;
    if (next !== undefined) {
      e.preventDefault();
      const id = dashboardTabs[next].id;
      setActiveTab(id);
      tabRefs.current[id]?.focus();
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      setStatus("Please verify that you are not a robot.");
      return;
    }
    try {
      // the textarea (`focus`) is sent as `reason`, which is the key the backend already expects
      await sendQuery({
        name: form.name,
        email: form.email,
        reason: form.focus,
        captchaToken,
      });
      setForm(defaultForm);
      setStatus("Thank you. Our team will be in touch shortly.");
    } catch (error) {
      console.log(error);
      setStatus("Something went wrong. Please try again.");
    } finally {
      // a reCAPTCHA token can only be used once
      captchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  return (
    <div className="gten-home">
      <a className="skip" href="#main">Skip to content</a>

      <header>
        <div className="wrap nav">
          <Link className="brand" to="/" aria-label="GTEN home">
            <span className="brand-mark" aria-hidden="true"></span>GTEN
          </Link>
          <button
            className="menu"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="navigation"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            Menu
          </button>
          <nav
            className={`navlinks${menuOpen ? " open" : ""}`}
            id="navigation"
            aria-label="Main navigation"
            onClick={closeMenu}
          >
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to}>{link.label}</Link>
            ))}
            <a className="btn" href={loginLink}>Login ↗</a>
          </nav>
        </div>
      </header>

      <main id="main">
        {/* Hero */}
        <section className="dark platform-hero" id="top">
          <div className="wrap">
            <div className="breadcrumb">GTEN / GAiNSS</div>
            <div className="platform-grid">
              <div>
                <span className="eyebrow">GAiNSS · Transformation Intelligence</span>
                <h1>Engineering Evidence.<br /><span>Transformation Intelligence.</span></h1>
                <p className="intro">Understand where delivery is at risk, quality needs attention and the engineering process can improve.</p>
                <p>GAiNSS connects user stories, test cases, automation, executions and defects in one engineering lifecycle view. Prebuilt dashboards and AI-assisted insights turn that evidence into signals teams can act on.</p>
                <div className="actions">
                  <Link className="btn" to="/contact">See GAiNSS in Action ↗</Link>
                  <Link className="btn secondary" to="/capabilities">Explore the Lifecycle ↓</Link>
                </div>
              </div>
              <div className="platform-map" role="img" aria-label="Engineering lifecycle evidence feeds GAiNSS intelligence, surfacing risk, schedule and quality signals">
                <span className="eyebrow">The intelligence layer</span>
                <div className="map-core">GAiNSS connects the lifecycle.</div>
                <div className="map-feed">
                  {mapFeed.map((item) => (<span key={item}>{item}</span>))}
                </div>
                <div className="map-output">
                  {mapOutput.map((item) => (<span key={item}>{item}</span>))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Role */}
        <section id="role">
          <div className="wrap bridge-grid">
            <div>
              <span className="eyebrow">Intelligence behind the assurance</span>
              <h2>Make the Engineering Lifecycle Easier to Understand.</h2>
            </div>
            <div className="bridge-text">
              <p>A delivery plan describes what should happen. Engineering evidence shows what is happening and where confidence is limited.</p>
              <p className="bridge-small">GAiNSS captures lifecycle information and intelligence metrics, bringing them into prebuilt dashboards for engineering teams and leaders. AI analyzes the evidence to identify process gaps and suggest opportunities to improve efficiency.</p>
              <p className="bridge-small">Within GTEN’s Transformation Assurance approach, this shared view helps teams connect delivery activity with the risks, decisions and improvements that matter to the transformation.</p>
            </div>
          </div>
        </section>

        {/* Lifecycle */}
        <section className="light lifecycle-section" id="lifecycle">
          <div className="wrap">
            <span className="eyebrow">Connected lifecycle</span>
            <h2>From the Intended Change to the Evidence Behind It.</h2>
            <p className="intro">Capture the relationship between what needs to be delivered, how it is validated and what the results reveal.</p>
            <div className="lifecycle" role="list">
              {lifecycle.map((item, i) => (
                <article className="lifecycle-item" role="listitem" key={item.title}>
                  <span className="number">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
            <div className="lifecycle-loop">↻ &nbsp; Execution results and defects inform the next validation and improvement decision.</div>
          </div>
        </section>

        {/* Signals */}
        <section id="signals">
          <div className="wrap">
            <span className="eyebrow">Engineering intelligence</span>
            <h2>See Where the Transformation Needs Attention.</h2>
            <p className="intro">Prebuilt engineering lifecycle dashboards highlight risk, schedule slippage and quality concerns so teams can focus their review and response.</p>
            <div className="lens-grid">
              {lenses.map((l) => (
                <article className="lens" key={l.title}>
                  <h3>{l.title}</h3>
                  <p>{l.text}</p>
                  <p className="question">{l.question}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Dashboards */}
        <section className="dark dashboard-section" id="dashboards">
          <div className="wrap">
            <span className="eyebrow">Prebuilt dashboards</span>
            <div className="dashboard-intro">
              <h2>One Lifecycle View.<br />More Informed Decisions.</h2>
              <p className="intro">GAiNSS brings engineering signals into dashboards that help teams review delivery health and identify where action is needed.</p>
            </div>
            <p className="preview-label">ILLUSTRATIVE CONCEPT PREVIEW · NOT A PRODUCT SCREENSHOT OR LIVE CLIENT DATA</p>
            <div className="dashboard">
              <div className="dashboard-header">
                <span className="dashboard-brand">GAiNSS / Engineering Intelligence</span>
                <small>CONCEPTUAL SIGNALS AND FINDINGS</small>
              </div>
              <div className="dashboard-tabs" role="tablist" aria-label="Explore dashboard concepts">
                {dashboardTabs.map((t, i) => (
                  <button
                    key={t.id}
                    ref={(el) => (tabRefs.current[t.id] = el)}
                    className="dashboard-tab"
                    type="button"
                    id={`tab-${t.id}`}
                    role="tab"
                    aria-selected={activeTab === t.id}
                    aria-controls={`panel-${t.id}`}
                    tabIndex={activeTab === t.id ? 0 : -1}
                    onClick={() => setActiveTab(t.id)}
                    onKeyDown={(e) => handleTabKeyDown(e, i)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {dashboardTabs.map((t) => (
                <div
                  className="dashboard-body"
                  role="tabpanel"
                  id={`panel-${t.id}`}
                  aria-labelledby={`tab-${t.id}`}
                  hidden={activeTab !== t.id}
                  key={t.id}
                >
                  <div>
                    <h3>{t.title}</h3>
                    <p>{t.text}</p>
                    <div className="signal-list">
                      {t.signals.map((s) => (<span key={s}>{s}</span>))}
                    </div>
                  </div>
                  <div className="finding">
                    <span className="eyebrow">Illustrative finding</span>
                    <h4>{t.finding}</h4>
                    <p>{t.findingText}</p>
                    <strong>{t.action}</strong>
                  </div>
                </div>
              ))}
              <div className="dashboard-footer">Examples explain the intended story and are illustrative only.</div>
            </div>
          </div>
        </section>

        {/* AI insights */}
        <section className="insight-section" id="ai-insights">
          <div className="wrap">
            <span className="eyebrow">AI-assisted improvement</span>
            <h2>Identify the Gap. Focus the Improvement.</h2>
            <p className="intro">GAiNSS uses AI to identify where the engineering process is lacking and share recommendations for greater efficiency. Teams use those findings to investigate, decide and improve.</p>
            <div className="insight-steps">
              {insightSteps.map((s) => (
                <article className="insight-step" key={s.title}>
                  <span className="number">{s.tag}</span>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </article>
              ))}
            </div>
            <div className="insight-example">
              <span className="eyebrow">Illustrative improvement scenario</span>
              <div>
                <h3>A signal worth investigating</h3>
                <p>A team repeatedly waits for manual validation near a delivery milestone. Execution progress and automation evidence suggest an opportunity to examine the validation workflow.</p>
              </div>
              <div>
                <h3>A focused team response</h3>
                <p>Review the scenarios, dependencies and effort involved. Prioritize suitable automation or sequencing changes, then compare cycle time and quality signals after the change.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Teams */}
        <section className="light" id="teams">
          <div className="wrap">
            <span className="eyebrow">Shared engineering context</span>
            <h2>Give Leaders and Teams a Common View.</h2>
            <div className="people-grid">
              {people.map((p) => (
                <article key={p.title}>
                  <h3>{p.title}</h3>
                  <p>{p.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Transformation Assurance fit */}
        <section className="dark fit-section" id="transformation-assurance">
          <div className="wrap fit-grid">
            <div>
              <span className="eyebrow">Transformation: Assured.</span>
              <h2>The Intelligence Platform Behind the Approach.</h2>
              <p className="intro">Transformation Assurance aligns engineering, validation and operations around intended business outcomes. GAiNSS adds lifecycle intelligence to that discipline.</p>
              <p className="fit-note">Engineering signals help guide the work. Business and operational measures provide the wider context needed to evaluate transformation outcomes.</p>
            </div>
            <ul className="fit-list">
              {fitSteps.map((s) => (
                <li key={s.title}>
                  <strong>{s.title}</strong>
                  <p>{s.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Demo / contact */}
        <section className="contact" id="demo">
          <div className="wrap cta-grid">
            <div>
              <span className="eyebrow">Request a demo</span>
              <h2>Bring Your Engineering Lifecycle Into Focus.</h2>
              <p className="intro">See how GAiNSS connects your lifecycle evidence, highlights delivery concerns and identifies opportunities to improve.</p>
              <p>Let’s explore the user stories, validation work and engineering decisions you want to understand better.</p>
            </div>
            <form className="demo-form" id="demo-form" onSubmit={handleSubmit}>
              <h3>Request a GAiNSS Demo</h3>
              <div className="field">
                <label htmlFor="name">Name</label>
                <input id="name" name="name" autoComplete="name" required value={form.name} onChange={handleChange} />
              </div>
              <div className="field">
                <label htmlFor="email">Work email</label>
                <input id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={handleChange} />
              </div>
              <div className="field">
                <label htmlFor="focus">What would you like better visibility into?</label>
                <textarea
                  id="focus"
                  name="focus"
                  placeholder="Delivery risk, schedule, quality or process efficiency"
                  value={form.focus}
                  onChange={handleChange}
                ></textarea>
              </div>
              <div className="field">
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={setCaptchaToken}
                  onExpired={() => setCaptchaToken(null)}
                  onErrored={() => setCaptchaToken(null)}
                />
              </div>
              <button className="btn" type="submit">Request a Demo ↗</button>
              <p className="form-status" id="form-status" aria-live="polite">{status}</p>
            </form>
          </div>
        </section>
      </main>

      <footer id="about">
        <div className="wrap">
          <div className="footer-grid">
            <div>
              <Link className="brand" to="/"><span className="brand-mark" aria-hidden="true"></span>GTEN</Link>
              <p>{aboutText}</p>
            </div>
            <div>
              <h3>GAiNSS</h3>
              <ul>
                {footerGainssLinks.map((l) => (
                  <li key={l.label}><Link to={l.to}>{l.label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Explore GTEN</h3>
              <ul>
                {footerExploreLinks.map((l) => (
                  <li key={l.label}><Link to={l.to}>{l.label}</Link></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="copyright">
            <span>© {new Date().getFullYear()} GTEN Technologies. All rights reserved.</span>
            <span>Transformation: Assured.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GtenHome;